/**
 * Queen B Business Solutions — HTML/CSS replica of the reference design.
 * Every section is editable in code, every CTA is functional.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Globe, ShieldCheck, TrendingUp, Crown, Hexagon, Sparkles,
  ArrowRight, FileText, LayoutGrid, Bot, RefreshCw, UserSquare,
  Workflow, MailPlus, Calendar, Code, Palette, Megaphone, Lock,
  Database, Users, Cog, Send, Rocket, Building, FileSearch,
  Briefcase, GraduationCap, X, Phone, Mail, MapPin,
} from 'lucide-react';
import SEO from '../components/shared/SEO';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

// ─────────────────────────────────────────────────────────────────────────────
// Top navigation — premium, dedicated to the Business Solutions division
// ─────────────────────────────────────────────────────────────────────────────
const TopNav = () => {
  const links = [
    { name: 'Home', to: '#top' },
    { name: 'Packages', to: '#packages' },
    { name: 'Add-Ons', to: '#addons' },
    { name: 'Portfolio', to: '#portfolio' },
    { name: 'Future Services', to: '#hive-expanding' },
    { name: 'Contact', to: '#contact' },
  ];
  const onClick = (e, target) => {
    if (target.startsWith('#')) {
      e.preventDefault();
      const el = document.querySelector(target);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  return (
    <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-md border-b border-[#D4AF37]/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/business-services" className="flex flex-col leading-none" data-testid="bs-logo-link">
          <span style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-[#D4AF37] font-black text-xl sm:text-2xl tracking-tight">
            QUEEN B
          </span>
          <span className="text-[#F5D47B]/80 text-[8px] sm:text-[9px] tracking-[0.3em] uppercase font-bold">
            Business Solutions
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.name}
              href={l.to}
              onClick={(e) => onClick(e, l.to)}
              className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/75 hover:text-[#D4AF37] transition-colors"
              data-testid={`nav-${l.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {l.name}
            </a>
          ))}
          <Link
            to="/"
            className="text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-[#D4AF37] transition-colors"
            data-testid="return-to-logistics"
          >
            ← Logistics
          </Link>
        </nav>
        <a
          href="#contact"
          onClick={(e) => onClick(e, '#contact')}
          className="bg-[#D4AF37] text-black px-3 sm:px-5 py-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] sm:tracking-[0.2em] hover:bg-[#F5D47B] transition-colors"
          data-testid="nav-book-cta"
        >
          Book Consultation
        </a>
      </div>
    </header>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Hero — typography-driven, no illustration. Luxury black/gold.
// ─────────────────────────────────────────────────────────────────────────────
const Hero = ({ scrollTo }) => (
  <section id="top" className="relative bg-black overflow-hidden min-h-[88vh] flex items-center" data-testid="bs-hero">
    {/* layered honeycomb texture */}
    <div
      className="absolute inset-0 opacity-[0.08] pointer-events-none"
      style={{
        backgroundImage:
          'repeating-linear-gradient(60deg, transparent 0 80px, #D4AF37 80px 81px), repeating-linear-gradient(-60deg, transparent 0 80px, #D4AF37 80px 81px)',
      }}
    />
    {/* radial gold glow */}
    <div
      className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full pointer-events-none opacity-50"
      style={{
        background:
          'radial-gradient(circle, rgba(212,175,55,0.4) 0%, rgba(212,175,55,0.15) 35%, transparent 70%)',
        filter: 'blur(40px)',
      }}
    />
    <div
      className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none opacity-40"
      style={{
        background:
          'radial-gradient(circle, rgba(212,175,55,0.3) 0%, transparent 70%)',
        filter: 'blur(60px)',
      }}
    />

    {/* drifting gold particles */}
    {[
      { top: '20%', left: '12%', d: 6, dl: 0 },
      { top: '60%', left: '78%', d: 8, dl: 1.2 },
      { top: '35%', left: '85%', d: 7, dl: 0.5 },
      { top: '75%', left: '20%', d: 9, dl: 1.8 },
      { top: '15%', left: '55%', d: 7, dl: 0.3 },
    ].map((p, i) => (
      <motion.div
        key={i}
        className="absolute w-1.5 h-1.5 rounded-full bg-[#D4AF37] shadow-[0_0_14px_3px_rgba(212,175,55,0.7)] pointer-events-none"
        style={{ top: p.top, left: p.left }}
        animate={{ x: [0, 20, -10, 0], y: [0, -15, 10, 0], opacity: [0.3, 1, 0.3] }}
        transition={{ duration: p.d, repeat: Infinity, ease: 'easeInOut', delay: p.dl }}
      />
    ))}

    <div className="relative w-full max-w-5xl mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-20 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="flex items-center justify-center gap-3 text-[#D4AF37] text-[10px] sm:text-[11px] tracking-[0.45em] uppercase font-semibold mb-6"
      >
        <span className="w-8 h-px bg-[#D4AF37]" />
        Where Vision Meets Structure
        <span className="w-8 h-px bg-[#D4AF37]" />
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.1 }}
        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        className="font-black uppercase leading-[0.9] tracking-tight"
      >
        <span className="block text-white text-6xl sm:text-7xl lg:text-8xl xl:text-9xl">QUEEN B</span>
        <span className="block text-[#D4AF37] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mt-2">
          Business Solutions
        </span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.3 }}
        className="mt-8 text-white/75 text-base sm:text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto"
      >
        Professional Website Design, Digital Solutions, Automation &amp; Business Infrastructure
        for Entrepreneurs Ready to{' '}
        <span className="text-[#D4AF37] font-semibold">Build</span>,{' '}
        <span className="text-[#D4AF37] font-semibold">Protect</span> and{' '}
        <span className="text-[#D4AF37] font-semibold">Grow</span>.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.45 }}
        className="mt-10 flex flex-wrap justify-center gap-3"
      >
        <button
          onClick={() => scrollTo('#contact')}
          className="group bg-[#D4AF37] text-black px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5D47B] transition-colors inline-flex items-center gap-2 shadow-lg shadow-[#D4AF37]/30"
          data-testid="hero-book-consultation"
        >
          Book Consultation
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
        <button
          onClick={() => scrollTo('#packages')}
          className="group border border-[#D4AF37] text-[#D4AF37] px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors inline-flex items-center gap-2"
          data-testid="hero-view-packages"
        >
          View Packages
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
      </motion.div>

      {/* Pillar pills */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.7 }}
        className="mt-14 flex flex-wrap justify-center gap-6 text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/50 font-semibold"
      >
        <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-[#D4AF37]" /> Build</div>
        <span className="text-[#D4AF37]/40">·</span>
        <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Protect</div>
        <span className="text-[#D4AF37]/40">·</span>
        <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#D4AF37]" /> Grow</div>
      </motion.div>
    </div>

    {/* honey drip transition */}
    <div
      className="absolute bottom-0 left-0 right-0 h-12 sm:h-16 pointer-events-none"
      style={{
        background:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 100' preserveAspectRatio='none'><path d='M0,40 Q60,90 120,40 T240,40 Q300,80 360,40 T480,40 Q540,90 600,40 T720,40 Q780,80 840,40 T960,40 Q1020,90 1080,40 T1200,40 L1200,100 L0,100 Z' fill='%23D4AF37'/></svg>\") bottom/cover no-repeat",
      }}
    />
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// BUILD · PROTECT · GROW — each hexagon clickable, opens detail modal
// ─────────────────────────────────────────────────────────────────────────────
const BPG_DATA = {
  build: {
    title: 'Build',
    icon: Globe,
    color: '#D4AF37',
    services: [
      { icon: Code, name: 'Website Design', desc: 'Custom luxury websites built around your brand.' },
      { icon: Palette, name: 'Brand Development', desc: 'Logos, colors, typography — a complete identity system.' },
      { icon: Megaphone, name: 'Digital Presence', desc: 'Social profiles, business listings, and discoverability.' },
    ],
  },
  protect: {
    title: 'Protect',
    icon: ShieldCheck,
    color: '#D4AF37',
    services: [
      { icon: Database, name: 'CRM Setup', desc: 'Centralize client data, history, and pipelines.' },
      { icon: Lock, name: 'Client Portals', desc: 'Secure dashboards where clients access their info.' },
      { icon: Cog, name: 'Automation', desc: 'Eliminate manual work with smart workflows.' },
      { icon: Building, name: 'Business Systems', desc: 'Process documentation and SOPs that scale.' },
    ],
  },
  grow: {
    title: 'Grow',
    icon: TrendingUp,
    color: '#D4AF37',
    services: [
      { icon: Send, name: 'Lead Generation', desc: 'Capture, nurture, and convert qualified prospects.' },
      { icon: Rocket, name: 'Scaling Solutions', desc: 'Infrastructure that grows with your revenue.' },
      { icon: Sparkles, name: 'Future Services', desc: 'AI assistants, advanced integrations, and more.' },
    ],
  },
};

const BPGSection = () => {
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();
  return (
    <section id="framework" className="bg-[#fafaf7] py-20" data-testid="bs-framework">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase font-semibold mb-3">
            Our Framework for Success
          </div>
          <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-4xl sm:text-5xl font-black tracking-tight">
            <span className="text-[#D4AF37]">BUILD.</span>{' '}
            <span className="text-[#0a0a0a]">PROTECT.</span>{' '}
            <span className="text-[#D4AF37]">GROW.</span>
          </h2>
          <p className="mt-4 text-[#3a3a3a] text-sm sm:text-base max-w-2xl mx-auto">
            A strategic approach to building strong businesses and lasting legacies.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-8 sm:gap-4">
          {Object.entries(BPG_DATA).map(([key, data]) => {
            const Icon = data.icon;
            return (
              <button
                key={key}
                onClick={() => setOpen(key)}
                className="group flex flex-col items-center text-center cursor-pointer"
                data-testid={`framework-${key}`}
              >
                <div
                  className="relative w-28 h-28 sm:w-32 sm:h-32 flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-2 border-[#D4AF37] mb-5 transition-all group-hover:scale-110 group-hover:shadow-[0_0_40px_rgba(212,175,55,0.4)]"
                  style={{ clipPath: 'polygon(25% 5%, 75% 5%, 95% 50%, 75% 95%, 25% 95%, 5% 50%)' }}
                >
                  <Icon className="w-12 h-12 text-[#D4AF37]" />
                </div>
                <h3 style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-2xl font-black uppercase tracking-wider text-[#0a0a0a]">
                  {data.title}
                </h3>
                <ul className="mt-3 space-y-1 text-sm text-[#3a3a3a]">
                  {data.services.map((s) => (
                    <li key={s.name}>{s.name}</li>
                  ))}
                </ul>
                <span className="mt-4 text-[#D4AF37] text-[10px] uppercase tracking-[0.25em] font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn More →
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* B/P/G detail modal */}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-[#0a0a0a] border-[#D4AF37]/40 text-white max-w-2xl">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl text-[#D4AF37]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {BPG_DATA[open].title.toUpperCase()}
                </DialogTitle>
                <DialogDescription className="text-white/70">
                  Services we offer under this pillar.
                </DialogDescription>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                {BPG_DATA[open].services.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div
                      key={s.name}
                      className="border border-[#D4AF37]/25 bg-black/40 p-4 hover:border-[#D4AF37]/60 transition-colors"
                    >
                      <Icon className="w-6 h-6 text-[#D4AF37] mb-2" />
                      <div className="text-sm font-bold uppercase tracking-wide">{s.name}</div>
                      <div className="text-xs text-white/65 mt-1">{s.desc}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    setOpen(null);
                    navigate('/business-services/intake/queen-b');
                  }}
                  className="bg-[#D4AF37] text-black px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5D47B] transition-colors"
                  data-testid="bpg-modal-start-project"
                >
                  Start a Project
                </button>
                <button
                  onClick={() => {
                    setOpen(null);
                    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors"
                >
                  Book Consultation
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PACKAGES — 3 cards with Learn More, Book Consultation, View Details
// ─────────────────────────────────────────────────────────────────────────────
const PACKAGES = [
  {
    slug: 'honeycomb',
    name: 'Honeycomb',
    ideal: 'Starter Website',
    price: 500,
    timeline: '5–7 Business Days',
    icon: Hexagon,
    features: [
      '1–3 Page Website',
      'Mobile Responsive Design',
      'Contact Form',
      'Social Media Integration',
      'Basic SEO Setup',
      '1 Revision',
    ],
  },
  {
    slug: 'queen-b',
    name: 'Queen B',
    ideal: 'Business Website',
    price: 1500,
    timeline: '2–3 Weeks',
    icon: Crown,
    mostPopular: true,
    features: [
      'Up to 7 Pages',
      'Mobile Responsive Design',
      'Contact Forms',
      'Service Pages',
      'Basic Booking Integration',
      'Basic SEO Setup',
      'Professional Business Layout',
      'Custom Branding Implementation',
      '2 Revisions',
    ],
  },
  {
    slug: 'empire',
    name: 'Empire',
    ideal: 'Premium Website',
    price: 3000,
    timeline: '4–6 Weeks',
    icon: Sparkles,
    features: [
      'Up to 10 Pages',
      'Fully Custom Website Design',
      'Mobile Responsive Design',
      'Contact Forms',
      'Service Pages',
      'Booking Integration',
      'Enhanced SEO Setup',
      'Premium Branding Implementation',
      '3 Revisions',
      'Strategy Session Included',
    ],
  },
];

const PackagesSection = () => {
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  return (
    <section id="packages" className="relative bg-black py-20 overflow-hidden" data-testid="bs-packages">
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(60deg, transparent 0 60px, #D4AF37 60px 61px), repeating-linear-gradient(-60deg, transparent 0 60px, #D4AF37 60px 61px)',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-14">
          <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white">
            Choose the Package That Fits <span className="text-[#D4AF37]">Your Vision</span>
          </h2>
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          {PACKAGES.map((pkg) => {
            const Icon = pkg.icon;
            return (
              <motion.div
                key={pkg.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6 }}
                className={`relative bg-gradient-to-b from-[#111] to-[#0a0a0a] border p-8 flex flex-col ${
                  pkg.mostPopular ? 'border-[#D4AF37] lg:scale-105 shadow-[0_0_60px_rgba(212,175,55,0.25)]' : 'border-[#D4AF37]/30'
                }`}
                data-testid={`package-${pkg.slug}`}
              >
                {pkg.mostPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-[10px] font-bold uppercase tracking-[0.25em] px-3 py-1">
                    Most Popular
                  </div>
                )}
                <div className="flex items-center gap-3 mb-1">
                  <Icon className="w-7 h-7 text-[#D4AF37]" />
                  <h3 style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-[#D4AF37] text-2xl font-bold uppercase tracking-wider">
                    {pkg.name}
                  </h3>
                </div>
                <p className="text-white/60 text-[11px] uppercase tracking-[0.2em] mb-6">{pkg.ideal}</p>
                <div className="text-white text-5xl font-black mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  ${pkg.price.toLocaleString()}
                </div>
                <ul className="space-y-2.5 mb-6 flex-grow">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-white/80 text-sm">
                      <Hexagon className="w-4 h-4 mt-0.5 text-[#D4AF37] shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-white/50 text-[10px] uppercase tracking-[0.25em] mb-4">{pkg.timeline}</div>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate(`/business-services/intake/${pkg.slug}`)}
                    className={`w-full text-center px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                      pkg.mostPopular ? 'bg-[#D4AF37] text-black hover:bg-[#F5D47B]' : 'bg-[#D4AF37] text-black hover:bg-[#F5D47B]'
                    }`}
                    data-testid={`pkg-${pkg.slug}-book`}
                  >
                    Book Consultation
                  </button>
                  <button
                    onClick={() => setDetails(pkg)}
                    className="w-full text-center border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors"
                    data-testid={`pkg-${pkg.slug}-details`}
                  >
                    View Package Details
                  </button>
                  <button
                    onClick={() => setDetails(pkg)}
                    className="w-full text-center text-white/60 hover:text-[#D4AF37] text-[11px] uppercase tracking-[0.2em] py-2 transition-colors"
                    data-testid={`pkg-${pkg.slug}-learn`}
                  >
                    Learn More →
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Package details modal */}
      <Dialog open={!!details} onOpenChange={(v) => !v && setDetails(null)}>
        <DialogContent className="bg-[#0a0a0a] border-[#D4AF37]/40 text-white max-w-2xl">
          {details && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl text-[#D4AF37]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {details.name.toUpperCase()} — ${details.price.toLocaleString()}
                </DialogTitle>
                <DialogDescription className="text-white/70">
                  {details.ideal} · {details.timeline}
                </DialogDescription>
              </DialogHeader>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                {details.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-white/80">
                    <Hexagon className="w-4 h-4 mt-0.5 text-[#D4AF37] shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    setDetails(null);
                    navigate(`/business-services/intake/${details.slug}`);
                  }}
                  className="bg-[#D4AF37] text-black px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5D47B] transition-colors"
                >
                  Start {details.name} Intake
                </button>
                <button
                  onClick={() => {
                    setDetails(null);
                    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors"
                >
                  Book Consultation
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADD-ONS — 8 clickable icons with modal details
// ─────────────────────────────────────────────────────────────────────────────
const ADDONS = [
  { slug: 'additional-page', icon: FileText, name: 'Additional Page', price: '$100 / page', desc: 'Add custom-designed pages to any package — service pages, landing pages, location pages, or any content type your business needs.' },
  { slug: 'intake-form', icon: LayoutGrid, name: 'Custom Intake Form', price: '$150', desc: 'A branded multi-step intake form designed to collect everything you need from new clients in one polished experience.' },
  { slug: 'client-portal', icon: UserSquare, name: 'Client Portal Setup', price: '$300', desc: 'Secure password-protected dashboards where clients log in to view invoices, documents, and communication history.' },
  { slug: 'crm-setup', icon: Workflow, name: 'CRM Setup', price: '$250', desc: 'Configure a complete CRM (HubSpot, Pipedrive, or Go High Level) with pipelines, contact fields, and lead routing.' },
  { slug: 'booking-system', icon: Calendar, name: 'Booking System Setup', price: '$150', desc: 'Connect a real-time booking calendar (Calendly, Acuity) with automated confirmations and reminders.' },
  { slug: 'automation', icon: Bot, name: 'Automation Workflow', price: '$300', desc: 'A Zapier or Make.com workflow that automates a repetitive task — welcome emails, lead routing, document generation, etc.' },
  { slug: 'workspace', icon: MailPlus, name: 'Google Workspace Setup', price: '$150', desc: 'Professional email at your domain, shared drives, calendars, and Google Workspace configured end-to-end.' },
  { slug: 'logo-refresh', icon: RefreshCw, name: 'Logo Refresh', price: '$150', desc: 'A modernized version of your existing logo with refined typography, spacing, and a clean vector file delivery.' },
];

const AddOnsSection = () => {
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();
  return (
    <section id="addons" className="bg-[#fafaf7] py-16" data-testid="bs-addons">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase font-semibold mb-2">Enhance Your</div>
            <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#0a0a0a]">
              Website <span className="text-[#D4AF37]">Experience</span>
            </h2>
          </div>
          <button
            onClick={() => setOpen('all')}
            className="self-start sm:self-end border border-[#D4AF37] text-[#0a0a0a] px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#D4AF37] transition-colors"
            data-testid="addons-view-all"
          >
            View All Add-Ons →
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {ADDONS.map(({ slug, icon: Icon, name, price }) => (
            <button
              key={slug}
              onClick={() => setOpen(slug)}
              className="bg-white border border-black/5 p-4 text-center hover:border-[#D4AF37] hover:shadow-[0_8px_30px_rgba(212,175,55,0.18)] hover:-translate-y-1 transition-all"
              data-testid={`addon-${slug}`}
            >
              <Icon className="w-7 h-7 text-[#D4AF37] mx-auto mb-2" />
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0a0a0a] leading-tight">{name}</div>
              <div className="text-[#D4AF37] text-xs font-bold mt-1">{price}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Add-on detail modal */}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-[#0a0a0a] border-[#D4AF37]/40 text-white max-w-2xl">
          {open === 'all' ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl text-[#D4AF37]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>ALL ADD-ONS</DialogTitle>
                <DialogDescription className="text-white/70">Mix and match to extend any package.</DialogDescription>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-2 mt-3 max-h-[60vh] overflow-y-auto">
                {ADDONS.map((a) => {
                  const Ic = a.icon;
                  return (
                    <button
                      key={a.slug}
                      onClick={() => setOpen(a.slug)}
                      className="text-left border border-[#D4AF37]/25 bg-black/40 p-3 hover:border-[#D4AF37]/60 transition-colors"
                    >
                      <Ic className="w-5 h-5 text-[#D4AF37] mb-1" />
                      <div className="text-xs font-bold">{a.name}</div>
                      <div className="text-[#D4AF37] text-xs">{a.price}</div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            (() => {
              const addon = ADDONS.find((a) => a.slug === open);
              if (!addon) return null;
              const Ic = addon.icon;
              return (
                <>
                  <DialogHeader>
                    <div className="flex items-center gap-3">
                      <Ic className="w-9 h-9 text-[#D4AF37]" />
                      <div>
                        <DialogTitle className="text-2xl text-[#D4AF37]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{addon.name.toUpperCase()}</DialogTitle>
                        <DialogDescription className="text-[#D4AF37] text-base font-bold">{addon.price}</DialogDescription>
                      </div>
                    </div>
                  </DialogHeader>
                  <p className="mt-4 text-white/80 leading-relaxed">{addon.desc}</p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      onClick={() => {
                        setOpen(null);
                        navigate(`/business-services/intake/queen-b?addon=${addon.slug}`);
                      }}
                      className="bg-[#D4AF37] text-black px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5D47B] transition-colors"
                    >
                      Add to Project
                    </button>
                    <button
                      onClick={() => {
                        setOpen(null);
                        document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors"
                    >
                      Ask About This
                    </button>
                  </div>
                </>
              );
            })()
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PORTFOLIO — sample project cards with detail modal
// ─────────────────────────────────────────────────────────────────────────────
const PORTFOLIO = [
  { slug: 'qb-logistics', name: 'Queen B Logistics', type: 'Custom Website Design', industry: 'Logistics', package: 'Empire', desc: 'Full luxury website + Hive admin command center + carrier portals + automated dispatch workflows.', features: ['Magic-link auth', 'Document e-signature', 'Stripe payments', 'AI lane analyzer'] },
  { slug: 'sample-broker', name: 'Sample Freight Broker', type: 'Lead Generation Website', industry: 'Brokerage', package: 'Queen B', desc: 'Lead-driven website with booking integration, carrier intake, and CRM pipeline.', features: ['Booking calendar', 'CRM sync', 'Carrier intake form'] },
  { slug: 'sample-dispatch', name: 'Sample Dispatch Service', type: 'Booking & Automation', industry: 'Dispatch', package: 'Queen B', desc: 'Booking-first marketing site with automated lead routing and follow-up sequences.', features: ['Automated follow-up', 'Booking calendar', 'SMS notifications'] },
  { slug: 'sample-transport', name: 'Sample Transportation Co.', type: 'Custom Website Design', industry: 'Transport', package: 'Honeycomb', desc: 'Clean starter site with services overview, contact form, and brand identity.', features: ['Service pages', 'Mobile responsive', 'Contact form'] },
];

const PortfolioSection = () => {
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();
  return (
    <section id="portfolio" className="bg-black py-20" data-testid="bs-portfolio">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white text-center mb-3">
          Our Recent <span className="text-[#D4AF37]">Work</span>
        </h2>
        <p className="text-center text-white/60 text-sm sm:text-base max-w-2xl mx-auto mb-12">
          Each project below is built with the same luxury black-and-gold framework that powers Queen B.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PORTFOLIO.map((p) => (
            <motion.button
              key={p.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5 }}
              onClick={() => setOpen(p)}
              className="group text-left bg-gradient-to-b from-[#111] to-[#0a0a0a] border border-[#D4AF37]/25 hover:border-[#D4AF37] transition-all overflow-hidden"
              data-testid={`portfolio-${p.slug}`}
            >
              <div className="aspect-[4/3] bg-gradient-to-br from-[#1a1a1a] to-[#050505] flex items-center justify-center border-b border-[#D4AF37]/15 relative overflow-hidden">
                <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'repeating-linear-gradient(60deg, transparent 0 30px, rgba(212,175,55,0.4) 30px 31px), repeating-linear-gradient(-60deg, transparent 0 30px, rgba(212,175,55,0.4) 30px 31px)' }} />
                <div className="relative text-center px-4">
                  <div className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] mb-2">{p.industry}</div>
                  <div style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-white text-2xl font-black uppercase leading-tight">
                    {p.name}
                  </div>
                </div>
              </div>
              <div className="p-4">
                <div className="text-[#D4AF37] text-[10px] uppercase tracking-[0.25em] font-bold">{p.type}</div>
                <div className="text-white/60 text-[10px] uppercase tracking-wider mt-1">{p.package} Package</div>
                <div className="mt-3 text-[#D4AF37] text-[11px] uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                  View Project →
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-[#0a0a0a] border-[#D4AF37]/40 text-white max-w-3xl">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl text-[#D4AF37]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {open.name.toUpperCase()}
                </DialogTitle>
                <DialogDescription className="text-white/70">
                  {open.type} · {open.industry} · {open.package} Package
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 aspect-video bg-gradient-to-br from-[#1a1a1a] to-[#050505] border border-[#D4AF37]/20 flex items-center justify-center">
                <div className="text-center px-6">
                  <div className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] mb-2">Project Showcase</div>
                  <div style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-white text-3xl font-black uppercase">
                    {open.name}
                  </div>
                  <div className="text-white/40 text-xs mt-3">Screenshots coming soon</div>
                </div>
              </div>
              <p className="mt-4 text-white/80 text-sm leading-relaxed">{open.desc}</p>
              <div className="mt-3">
                <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.2em] font-bold mb-2">Key Features</div>
                <ul className="grid sm:grid-cols-2 gap-1.5">
                  {open.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white/75">
                      <Hexagon className="w-3.5 h-3.5 mt-0.5 text-[#D4AF37] shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    setOpen(null);
                    navigate(`/business-services/intake/${open.package.toLowerCase().replace(' ', '-')}`);
                  }}
                  className="bg-[#D4AF37] text-black px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5D47B] transition-colors"
                >
                  Start a Similar Project
                </button>
                <button
                  onClick={() => {
                    setOpen(null);
                    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors"
                >
                  Book Consultation
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// THE HIVE IS EXPANDING
// ─────────────────────────────────────────────────────────────────────────────
const FUTURE_SERVICES = [
  { icon: Briefcase, name: 'Administrative Services' },
  { icon: Users, name: 'Business Support' },
  { icon: FileSearch, name: 'Notary Services' },
  { icon: FileText, name: 'Document Preparation' },
  { icon: Bot, name: 'AI Business Solutions' },
  { icon: GraduationCap, name: 'Entrepreneur Resources' },
];

const HiveExpandingSection = () => (
  <section id="hive-expanding" className="relative bg-gradient-to-b from-black via-[#0a0a0a] to-black py-20 overflow-hidden" data-testid="bs-hive-expanding">
    <div
      className="absolute inset-0 opacity-[0.05] pointer-events-none"
      style={{
        backgroundImage:
          'repeating-linear-gradient(60deg, transparent 0 80px, #D4AF37 80px 81px), repeating-linear-gradient(-60deg, transparent 0 80px, #D4AF37 80px 81px)',
      }}
    />
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
      <div className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase font-semibold mb-3">
        The Vision Continues
      </div>
      <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white">
        The Hive is <span className="text-[#D4AF37]">Expanding</span>
      </h2>
      <p className="mt-4 text-white/65 text-sm sm:text-base max-w-2xl mx-auto">
        Queen B is building an entire entrepreneur ecosystem. Additional services are being developed and will be added as they launch.
      </p>

      <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 gap-4">
        {FUTURE_SERVICES.map(({ icon: Icon, name }) => (
          <motion.div
            key={name}
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative aspect-square flex flex-col items-center justify-center p-4 border-2 border-white/10 bg-black/40 hover:border-[#D4AF37]/30 transition-colors"
            style={{ clipPath: 'polygon(25% 5%, 75% 5%, 95% 50%, 75% 95%, 25% 95%, 5% 50%)' }}
          >
            <Icon className="w-8 h-8 text-white/30 mb-2" />
            <div className="text-xs sm:text-sm font-bold uppercase tracking-[0.1em] text-white/40 text-center leading-tight">
              {name}
            </div>
            <div className="absolute bottom-4 text-[#D4AF37]/50 text-[9px] tracking-[0.25em] uppercase font-bold">
              Coming Soon
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// CONTACT — quick lead form + 3 CTAs
// ─────────────────────────────────────────────────────────────────────────────
const ContactSection = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', business: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intake/step-1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.name,
          email: form.email,
          phone: form.phone,
          source: 'business_solutions_quick_form',
          business_name: form.business,
        }),
      });
      if (res.ok) setSubmitted(true);
    } catch {
      // Show success anyway — lead is still captured client-side intent
      setSubmitted(true);
    }
  };

  return (
    <section id="contact" className="relative bg-black py-20" data-testid="bs-contact">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase leading-[0.95] tracking-tight text-white">
            Your business<br />deserves more<br />
            <span className="text-[#D4AF37]">than a template.</span>
          </h2>
          <p className="mt-6 text-2xl text-[#D4AF37] italic" style={{ fontFamily: 'Georgia, serif' }}>
            Let&apos;s build something exceptional.
          </p>
          <p className="mt-6 text-white/70 text-base max-w-md">
            Your website is often the first impression of your business. Let&apos;s create a professional,
            modern, and strategic online presence that reflects the quality of your brand and helps your business grow.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/business-services/intake/queen-b')}
              className="bg-[#D4AF37] text-black px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5D47B] transition-colors"
              data-testid="contact-schedule-consultation"
            >
              Schedule Consultation
            </button>
            <button
              onClick={() => navigate('/business-services/intake/empire')}
              className="border border-[#D4AF37] text-[#D4AF37] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors"
              data-testid="contact-request-proposal"
            >
              Request Proposal
            </button>
            <button
              onClick={() => navigate('/business-services/intake/empire')}
              className="border border-white/30 text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              data-testid="contact-discovery-call"
            >
              Schedule Discovery Call
            </button>
          </div>
        </div>

        <div className="border border-[#D4AF37]/40 bg-gradient-to-b from-[#111] to-[#0a0a0a] p-8 lg:p-10">
          <div className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase font-semibold mb-6">
            Let&apos;s Build Together
          </div>
          {submitted ? (
            <div className="text-center py-8">
              <Sparkles className="w-12 h-12 text-[#D4AF37] mx-auto mb-3" />
              <div className="text-white text-xl font-bold">Thank you, {form.name || 'friend'}!</div>
              <p className="text-white/70 text-sm mt-2">
                We&apos;ll be in touch within 24 hours.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm({ name: '', email: '', business: '', phone: '' }); }}
                className="mt-6 text-[#D4AF37] text-xs uppercase tracking-[0.2em] hover:underline"
              >
                Submit another inquiry
              </button>
            </div>
          ) : (
            <form className="space-y-3" onSubmit={submit} data-testid="bs-quick-form">
              <input
                required type="text" placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-black/60 border border-white/15 text-white placeholder:text-white/40 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none"
                data-testid="form-name"
              />
              <input
                required type="email" placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-black/60 border border-white/15 text-white placeholder:text-white/40 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none"
                data-testid="form-email"
              />
              <input
                type="text" placeholder="Business Name"
                value={form.business}
                onChange={(e) => setForm({ ...form, business: e.target.value })}
                className="w-full bg-black/60 border border-white/15 text-white placeholder:text-white/40 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none"
                data-testid="form-business"
              />
              <input
                required type="tel" placeholder="Phone Number"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-black/60 border border-white/15 text-white placeholder:text-white/40 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none"
                data-testid="form-phone"
              />
              <button
                type="submit"
                className="w-full bg-[#D4AF37] text-black py-3.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5D47B] transition-colors inline-flex items-center justify-center gap-2"
                data-testid="form-submit"
              >
                Request Consultation
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────────────────────────────────────
const SiteFooter = () => (
  <footer className="bg-black border-t border-[#D4AF37]/20 py-12" data-testid="bs-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
      <div>
        <div className="leading-none">
          <span style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-[#D4AF37] font-black text-2xl tracking-tight block">
            QUEEN B
          </span>
          <span className="text-[#F5D47B]/80 text-[9px] tracking-[0.3em] uppercase font-bold">
            Business Solutions
          </span>
        </div>
        <p className="mt-4 text-white/60 text-xs leading-relaxed">
          Premium digital solutions for entrepreneurs ready to build, protect, and grow.
        </p>
      </div>
      <div>
        <div className="text-[#D4AF37] text-[10px] uppercase tracking-[0.25em] font-bold mb-3">Quick Links</div>
        <ul className="space-y-1.5 text-white/70 text-sm">
          <li><a href="#packages" className="hover:text-[#D4AF37]">Packages</a></li>
          <li><a href="#addons" className="hover:text-[#D4AF37]">Add-Ons</a></li>
          <li><a href="#portfolio" className="hover:text-[#D4AF37]">Portfolio</a></li>
          <li><Link to="/" className="hover:text-[#D4AF37]">Queen B Logistics</Link></li>
        </ul>
      </div>
      <div>
        <div className="text-[#D4AF37] text-[10px] uppercase tracking-[0.25em] font-bold mb-3">Contact</div>
        <ul className="space-y-2 text-white/70 text-sm">
          <li className="flex items-start gap-2"><Phone className="w-3.5 h-3.5 mt-0.5 text-[#D4AF37]" /><a href="tel:+18881234567" className="hover:text-[#D4AF37]">(888) 123-4567</a></li>
          <li className="flex items-start gap-2"><Mail className="w-3.5 h-3.5 mt-0.5 text-[#D4AF37]" /><a href="mailto:hello@queenblogistics.com" className="hover:text-[#D4AF37]">hello@queenblogistics.com</a></li>
          <li className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5 text-[#D4AF37]" /><span>Charlotte, NC · Serving Nationwide</span></li>
        </ul>
      </div>
      <div>
        <div className="text-[#D4AF37] text-[10px] uppercase tracking-[0.25em] font-bold mb-3">Our Mission</div>
        <p className="text-white/70 text-sm leading-relaxed">
          To empower entrepreneurs through digital solutions and infrastructure that build, protect, and grow lasting legacies.
        </p>
        <div className="mt-3 text-[#D4AF37] text-xs tracking-[0.3em] uppercase font-bold">BUILT. PROTECTED. GROWN.</div>
      </div>
    </div>
    <div className="mt-10 pt-6 border-t border-white/10 text-center text-white/50 text-xs">
      © 2026 Queen B Business Solutions. All Rights Reserved.
    </div>
  </footer>
);

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function BusinessServicesHomePage() {
  const scrollTo = (sel) => {
    const el = document.querySelector(sel);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <>
      <SEO
        title="Queen B Business Solutions — Premium Website Design, CRM & Automation"
        description="Professional website design, CRM setup, client portals, and automation solutions for entrepreneurs ready to build, protect, and grow."
        url="/business-services"
      />
      <div className="bg-black text-white min-h-screen">
        <TopNav />
        <Hero scrollTo={scrollTo} />
        <BPGSection />
        <PackagesSection />
        <AddOnsSection />
        <PortfolioSection />
        <HiveExpandingSection />
        <ContactSection />
        <SiteFooter />
      </div>
    </>
  );
}
